/**
 * CR385: Robust parser for resolve_notes (JSON or legacy string)
 * Used by Pipeline Step Outputs and diagnostics drawers
 */

export interface ParsedResolveNotes {
  notes: string;
  acquisition_engine: "html_text" | "firecrawl_v2" | "unknown";
  engine_reason: string | null;
  url_lineage: {
    configured_source_id: string | null;
    configured_url: string | null;
    requested_url: string | null;
    final_url: string | null;
  };
  firecrawl: {
    endpoint: string;
    final_url: string | null;
    status_code: number | null;
    javascript_returns: unknown[] | null;
    screenshots: string[] | null;
    warning: string | null;
    // CR385: Links diagnostics
    links_count: number | null;
    links_sample: string[] | null;
    links_shape: string | null;
    // CR400: Error page detection
    looks_like_error_page: boolean | null;
  } | null;
  // CR385+CR386: SCAN diagnostics (present on SCAN steps)
  scan_diag: {
    links_in_count: number;
    links_shape: string | null;
    // CR386: Enhanced link filter diagnostics
    links_kept_count: number | null;
    links_dropped_count: number | null;
    drop_reason_counts: Record<string, number> | null;
    kept_samples: string[] | null;
    dropped_samples: string[] | null;
    candidates_emitted_count: number;
    gate_reason: string;
    link_samples: string[];
    text_char_count?: number | null;
    fallback_url_used?: string | null;
    // CR386: Link seeding diagnostics
    seeded_count?: number | null;
    relevant_links_count?: number | null;
    relevant_links_sample?: string[] | null;
  } | null;
  // CR388: Blocker detection diagnostics
  blockers_detected: {
    type: string;
    confidence: number;
    evidence: string[];
  }[] | null;
  // CR388: Variant runner diagnostics
  variant_runner: {
    pattern: string;
    variants_tried: {
      variant_id: string;
      actions?: string[];
      success_checks?: Record<string, boolean>;
      text_length: number;
      score: number;
      failure_reason: string | null;
      duration_ms?: number;
    }[];
    selected_variant_id: string | null;
    selected_score: number;
    selected_reason?: string;
  } | null;
  // CR406: Cookbook diagnostics
  cookbook: {
    attempted: boolean;
    recovered: boolean;
    selected_template_id: string | null;
    selected_template_name: string | null;
    vendor_hint: string | null;
    gate_type: string | null;
    actions_count: number;
    original_char_count: number;
    recovered_char_count: number | null;
    skip_reason?: string;
    attempts?: {
      template_id: string;
      template_name: string;
      gate_type: string | null;
      actions_count: number;
      recovered_char_count: number | null;
      cookie_wall_still_present: boolean | null;
      outcome: string;
      error?: string;
    }[];
  } | null;
  // CR389/CR390: Gate Detective diagnostics
  gate_detective: {
    attempted: boolean;
    recovered: boolean;
    gate_type: string | null;
    vendor_hint: string | null;
    actions_count: number;
    original_char_count: number;
    recovered_char_count: number | null;
    hard_blocked?: boolean;
    used_active_pattern: boolean;
    pattern_id?: string;
    template_id?: string;
    // CR395: Auth debug flags
    auth_headers_present?: { lovable_api_key: boolean; authorization: boolean };
    // CR394: Failure reasons
    reason?: string;
    skip_reason?: string;
    error_snippet?: string;
  } | null;
  // Legacy fields that may be present
  recipe_used?: Record<string, unknown> | null;
  recipe_attempts?: unknown[];
  cookie_wall_detected?: boolean;
  /** True if the source was a legacy plain-text string */
  _legacy: boolean;
  /** The raw parsed object (for Copy Debug Bundle) */
  _raw: Record<string, unknown>;
}

/**
 * Parse resolve_notes from either jsonb (object), JSON string, or legacy plain text
 */
export function parseResolveNotes(raw: unknown): ParsedResolveNotes {
  const empty: ParsedResolveNotes = {
    notes: "",
    acquisition_engine: "unknown",
    engine_reason: null,
    url_lineage: { configured_source_id: null, configured_url: null, requested_url: null, final_url: null },
    firecrawl: null,
    scan_diag: null,
    blockers_detected: null,
    variant_runner: null,
    cookbook: null,
    gate_detective: null,
    _legacy: true,
    _raw: {},
  };

  if (!raw) return empty;

  // Already an object (jsonb column)
  let obj: Record<string, unknown>;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    obj = raw as Record<string, unknown>;
  } else if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed.startsWith("{")) {
      try {
        obj = JSON.parse(trimmed);
      } catch {
        return { ...empty, notes: raw as string, _raw: { notes: raw } };
      }
    } else {
      // Legacy plain text
      return { ...empty, notes: raw as string, _raw: { notes: raw } };
    }
  } else {
    return empty;
  }

  const urlLineage = (obj.url_lineage as Record<string, unknown>) ?? {};
  const fc = obj.firecrawl as Record<string, unknown> | null;

  // Infer engine from available fields
  let engine = (obj.acquisition_engine as string) ?? null;
  if (!engine) {
    // Heuristic: if firecrawl block exists or recipe_attempts exist, it was firecrawl
    if (fc || (Array.isArray(obj.recipe_attempts) && (obj.recipe_attempts as unknown[]).length > 0)) {
      engine = "firecrawl_v2";
    } else {
      engine = "unknown";
    }
  }

  return {
    notes: (obj.notes as string) ?? "",
    acquisition_engine: engine as ParsedResolveNotes["acquisition_engine"],
    engine_reason: (obj.engine_reason as string) ?? null,
    url_lineage: {
      configured_source_id: (urlLineage.configured_source_id as string) ?? null,
      configured_url: (urlLineage.configured_url as string) ?? null,
      requested_url: (urlLineage.requested_url as string) ?? null,
      final_url: (urlLineage.final_url as string) ?? null,
    },
    firecrawl: fc
      ? {
          endpoint: (fc.endpoint as string) ?? "v2/scrape",
          final_url: (fc.final_url as string) ?? null,
          status_code: (fc.status_code as number) ?? null,
          javascript_returns: (fc.javascript_returns as unknown[]) ?? null,
          screenshots: (fc.screenshots as string[]) ?? null,
          warning: (fc.warning as string) ?? null,
          // CR385: Links diagnostics
           links_count: (fc.links_count as number) ?? null,
           links_sample: (fc.links_sample as string[]) ?? null,
           links_shape: (fc.links_shape as string) ?? null,
           looks_like_error_page: (fc.looks_like_error_page as boolean) ?? null,
        }
      : null,
    // CR385: SCAN diagnostics
    scan_diag: (() => {
      const sd = obj.scan_diag as Record<string, unknown> | null;
      if (!sd) return null;
      return {
        links_in_count: (sd.links_in_count as number) ?? 0,
        links_shape: (sd.links_shape as string) ?? null,
        links_kept_count: (sd.links_kept_count as number) ?? null,
        links_dropped_count: (sd.links_dropped_count as number) ?? null,
        drop_reason_counts: (sd.drop_reason_counts as Record<string, number>) ?? null,
        kept_samples: (sd.kept_samples as string[]) ?? null,
        dropped_samples: (sd.dropped_samples as string[]) ?? null,
        candidates_emitted_count: (sd.candidates_emitted_count as number) ?? 0,
        gate_reason: (sd.gate_reason as string) ?? "UNKNOWN",
        link_samples: (sd.link_samples as string[]) ?? [],
        text_char_count: (sd.text_char_count as number) ?? null,
        fallback_url_used: (sd.fallback_url_used as string) ?? null,
        seeded_count: (sd.seeded_count as number) ?? null,
        relevant_links_count: (sd.relevant_links_count as number) ?? null,
        relevant_links_sample: (sd.relevant_links_sample as string[]) ?? null,
      };
    })(),
    // CR388: Blocker detection
    blockers_detected: Array.isArray(obj.blockers_detected)
      ? (obj.blockers_detected as { type: string; confidence: number; evidence: string[] }[])
      : null,
    // CR388: Variant runner diagnostics
    variant_runner: (() => {
      const vr = obj.variant_runner as Record<string, unknown> | null;
      if (!vr) return null;
      return {
        pattern: (vr.pattern as string) ?? "UNKNOWN",
        variants_tried: Array.isArray(vr.variants_tried)
          ? (vr.variants_tried as any[]).map(v => ({
              variant_id: v.variant_id ?? "unknown",
              actions: v.actions,
              success_checks: v.success_checks,
              text_length: v.text_length ?? 0,
              score: v.score ?? 0,
              failure_reason: v.failure_reason ?? null,
              duration_ms: v.duration_ms,
            }))
          : [],
        selected_variant_id: (vr.selected_variant_id as string) ?? null,
        selected_score: (vr.selected_score as number) ?? 0,
        selected_reason: (vr.selected_reason as string) ?? undefined,
      };
    })(),
    // CR390: Gate Detective diagnostics
    gate_detective: (() => {
      const gd = obj.gate_detective as Record<string, unknown> | null;
      if (!gd) return null;
      return {
        attempted: (gd.attempted as boolean) ?? false,
        recovered: (gd.recovered as boolean) ?? false,
        gate_type: (gd.gate_type as string) ?? null,
        vendor_hint: (gd.vendor_hint as string) ?? null,
        actions_count: (gd.actions_count as number) ?? 0,
        original_char_count: (gd.original_char_count as number) ?? 0,
        recovered_char_count: (gd.recovered_char_count as number) ?? null,
        hard_blocked: (gd.hard_blocked as boolean) ?? undefined,
        used_active_pattern: (gd.used_active_pattern as boolean) ?? false,
        pattern_id: (gd.pattern_id as string) ?? undefined,
        template_id: (gd.template_id as string) ?? undefined,
        // CR395: Auth debug flags
        auth_headers_present: gd.auth_headers_present as { lovable_api_key: boolean; authorization: boolean } | undefined,
        // CR394: Failure reasons
        reason: (gd.reason as string) ?? undefined,
        skip_reason: (gd.skip_reason as string) ?? undefined,
        error_snippet: (gd.error_snippet as string) ?? undefined,
      };
    })(),
    // CR411: Cookbook diagnostics
    cookbook: (() => {
      const cb = obj.cookbook as Record<string, unknown> | null;
      if (!cb) return null;
      return {
        attempted: (cb.attempted as boolean) ?? false,
        recovered: (cb.recovered as boolean) ?? false,
        selected_template_id: (cb.selected_template_id as string) ?? null,
        selected_template_name: (cb.selected_template_name as string) ?? null,
        vendor_hint: (cb.vendor_hint as string) ?? null,
        gate_type: (cb.gate_type as string) ?? null,
        actions_count: (cb.actions_count as number) ?? 0,
        original_char_count: (cb.original_char_count as number) ?? 0,
        recovered_char_count: (cb.recovered_char_count as number) ?? null,
        skip_reason: (cb.skip_reason as string) ?? undefined,
        attempts: Array.isArray(cb.attempts) ? (cb.attempts as any[]) : undefined,
      };
    })(),
    recipe_used: (obj.recipe_used as Record<string, unknown>) ?? null,
    recipe_attempts: (obj.recipe_attempts as unknown[]) ?? [],
    cookie_wall_detected: (obj.cookie_wall_detected as boolean) ?? false,
    _legacy: !obj.acquisition_engine && !obj.scan_diag,
    _raw: obj,
  };
}

/**
 * Get display label for engine
 */
export function getEngineLabel(engine: string): string {
  switch (engine) {
    case "firecrawl_v2":
      return "Firecrawl v2";
    case "html_text":
      return "HTML Fetch";
    default:
      return "Legacy / Unknown";
  }
}

/**
 * Get badge variant for engine
 */
export function getEngineBadgeVariant(engine: string): "default" | "secondary" | "outline" {
  switch (engine) {
    case "firecrawl_v2":
      return "default";
    case "html_text":
      return "secondary";
    default:
      return "outline";
  }
}

/**
 * Build debug bundle string for clipboard copy
 */
export function buildDebugBundle(
  step: {
    id?: string;
    merchant_id?: string | null;
    status?: string;
    text_char_count?: number | null;
    resolve_extraction_quality_score?: number | null;
    error_message?: string | null;
  },
  parsed: ParsedResolveNotes
): string {
  const bundle = {
    step_id: step.id ?? null,
    merchant_id: step.merchant_id ?? null,
    status: step.status ?? null,
    acquisition_engine: parsed.acquisition_engine,
    engine_reason: parsed.engine_reason,
    url_lineage: parsed.url_lineage,
    configured_source_id: parsed.url_lineage.configured_source_id,
    firecrawl: parsed.firecrawl,
    // CR385: Include links + scan_diag in debug bundle
    firecrawl_links_count: parsed.firecrawl?.links_count ?? null,
    firecrawl_links_sample: parsed.firecrawl?.links_sample ?? null,
    scan_diag: parsed.scan_diag,
    // CR386: Enhanced link filter diagnostics in debug bundle
    scan_links_kept: parsed.scan_diag?.links_kept_count ?? null,
    scan_links_dropped: parsed.scan_diag?.links_dropped_count ?? null,
    scan_drop_reasons: parsed.scan_diag?.drop_reason_counts ?? null,
    scan_kept_samples: parsed.scan_diag?.kept_samples ?? null,
    scan_dropped_samples: parsed.scan_diag?.dropped_samples ?? null,
    scan_fallback_url: parsed.scan_diag?.fallback_url_used ?? null,
    text_char_count: step.text_char_count ?? null,
    quality_score: step.resolve_extraction_quality_score ?? null,
    error_message: step.error_message ?? null,
  };
  return JSON.stringify(bundle, null, 2);
}
